import { ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSession } from "@/lib/auth-client";
import { canViewAll } from "@/lib/permissions";

/**
 * In-app Help / User Guide. Content is authored as structured data below and
 * rendered by a small typed renderer — the app has no markdown renderer
 * dependency, so guide copy lives as JSX-friendly blocks rather than markdown
 * strings.
 *
 * The Admin Guide only renders for `role === "admin"`. That gate is a UI
 * convenience so non-admins aren't shown instructions for actions they can't
 * take — it is NOT a security boundary. Every admin action is enforced
 * server-side regardless of what this page shows.
 */

type GuideBlock =
  | { kind: "p"; text: string }
  | { kind: "subheading"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "terms"; items: { term: string; desc: string }[] };

interface GuideSection {
  id: string;
  title: string;
  blocks: GuideBlock[];
}

const userGuideSections: GuideSection[] = [
  {
    id: "orientation",
    title: "Finding your way around",
    blocks: [
      {
        kind: "p",
        text: "Enable is your organization's operating system for people, roles, and capability. Everything is reachable from the left sidebar and the topbar at the top of every page.",
      },
      { kind: "subheading", text: "The sidebar" },
      {
        kind: "p",
        text: "Navigation is grouped so related pages sit together:",
      },
      {
        kind: "terms",
        items: [
          {
            term: "My Workspace",
            desc: "Your personal home — My Dashboard, My Certifications, and this Help guide. The app version at the bottom of the sidebar opens What's New.",
          },
          {
            term: "People & Org",
            desc: "The People directory, Roles, and the Organization structure and Org Chart.",
          },
          {
            term: "Capability",
            desc: "Skills, Competencies, Certifications, the Capability Matrix, and Compare Roles.",
          },
          {
            term: "Administration",
            desc: "The Org Health dashboard and Settings — visible only to administrators.",
          },
        ],
      },
      {
        kind: "p",
        text: "You can collapse the sidebar to icons using the toggle at the top of it to give yourself more room. On a phone it slides in over the page instead.",
      },
      { kind: "subheading", text: "The topbar" },
      {
        kind: "terms",
        items: [
          {
            term: "Breadcrumbs",
            desc: "Show where you are and let you jump back up the hierarchy — click an earlier crumb to return to it.",
          },
          {
            term: "Theme toggle",
            desc: "Switch between light and dark mode. Your choice is remembered on this device.",
          },
          {
            term: "Account menu",
            desc: "The circle with your initial (top-right). Opens quick links to your dashboard, this Help guide, and Sign out.",
          },
        ],
      },
    ],
  },
  {
    id: "my-dashboard",
    title: "My Dashboard",
    blocks: [
      {
        kind: "p",
        text: "My Dashboard is your landing page after signing in. It gives you a personalized snapshot of what's relevant to you right now:",
      },
      {
        kind: "terms",
        items: [
          { term: "Greeting", desc: "A quick welcome with your name and the current date." },
          {
            term: "Quick links",
            desc: "Shortcuts to the pages you use most, so common tasks are one click away.",
          },
          {
            term: "Announcements",
            desc: "Organization-wide notices posted by administrators. The latest ones appear here.",
          },
          {
            term: "Your direct reports",
            desc: "If people report to you, they're listed so you can jump straight to their records.",
          },
          {
            term: "New joiners",
            desc: "Recently added team members, to help you welcome and orient them.",
          },
          {
            term: "Certifications expiring soon",
            desc: "A heads-up on certifications approaching their expiry date so nothing lapses unnoticed.",
          },
        ],
      },
    ],
  },
  {
    id: "my-certifications",
    title: "My Certifications",
    blocks: [
      {
        kind: "p",
        text: "The My Certifications page lists every certification assigned to you. For each one you can see its current status and expiry date, and open any proof that has been uploaded.",
      },
      {
        kind: "p",
        text: "This view is read-only — assigning certifications, changing status, and uploading proof are administrator actions. If something looks wrong, contact an administrator.",
      },
    ],
  },
  {
    id: "finding-things",
    title: "Finding people and things",
    blocks: [
      {
        kind: "p",
        text: "The People directory and the Roles, Skills, Competencies, and Certifications lists all work the same way, so once you know one you know them all.",
      },
      {
        kind: "terms",
        items: [
          {
            term: "Search",
            desc: "Type in the search box to narrow the list as you type.",
          },
          {
            term: "Filters",
            desc: "Use the filter controls to narrow by one or more values at once (multi-select) — for example several roles or statuses together.",
          },
          {
            term: "Sorting",
            desc: "Click a column header to sort by it; click again to reverse the direction.",
          },
        ],
      },
      { kind: "subheading", text: "Your view is saved in the address bar" },
      {
        kind: "p",
        text: "Whatever you search, filter, and sort is written into the page's web address. That means you can bookmark a filtered view, share the link with a colleague and they'll see the same list, and use your browser's Back button to return to exactly what you were looking at.",
      },
      { kind: "subheading", text: "Keyboard navigation" },
      {
        kind: "p",
        text: "List rows are keyboard-accessible: use Tab to move focus onto a row and press Enter to open it. You don't have to reach for the mouse.",
      },
    ],
  },
  {
    id: "detail-pages",
    title: "Detail pages and history",
    blocks: [
      {
        kind: "p",
        text: "Clicking an entry in any list opens its detail page. Detail pages group information into tabs — for a person that includes their profile, roles, and capability; other records show the details relevant to them.",
      },
      {
        kind: "terms",
        items: [
          {
            term: "History tab",
            desc: 'Every record has a History tab showing an audit trail of changes made to it — what changed, and when. It\'s the quickest way to answer "who changed this and when?".',
          },
        ],
      },
    ],
  },
  {
    id: "capability",
    title: "Capability",
    blocks: [
      {
        kind: "p",
        text: "The Capability page answers two questions, one per tab. Gaps shows where a team falls short of what their roles require; the Requirements matrix shows what the roles ask for in the first place.",
      },
      {
        kind: "p",
        text: "Gaps reads scores from closed review rounds. Only capabilities someone has actually been rated on are counted — a capability nobody has rated is left out rather than shown as a blank, so the page reads as a list of findings. It defaults to your own direct reports; pick a role to look across a whole discipline.",
      },
      {
        kind: "p",
        text: "The Requirements matrix is a grid that lays roles against the skills and competencies they require. Reading across a role's row shows everything that role calls for; reading down a capability's column shows every role that needs it.",
      },
    ],
  },
  {
    id: "compare-roles",
    title: "Compare Roles (career pathing)",
    blocks: [
      {
        kind: "p",
        text: "Compare Roles helps you plan growth. Pick your current role and a target role, and Enable shows you how they line up:",
      },
      {
        kind: "terms",
        items: [
          { term: "Overlapping", desc: "Requirements both roles share — what you already bring." },
          {
            term: "Gained",
            desc: "Requirements the target role adds that your current role doesn't have — your development focus.",
          },
          {
            term: "No longer needed",
            desc: "Requirements in your current role that the target role doesn't call for.",
          },
        ],
      },
      {
        kind: "p",
        text: "Use it to have a concrete conversation about the gap between where you are and where you want to go.",
      },
    ],
  },
  {
    id: "organization",
    title: "Organization and Org Chart",
    blocks: [
      {
        kind: "p",
        text: "The Organization page lets you browse the reporting hierarchy. The Org Chart draws that hierarchy visually.",
      },
      {
        kind: "terms",
        items: [
          { term: "Zoom and fit", desc: "Zoom in and out, or fit the whole chart to your screen." },
          {
            term: "Find a person",
            desc: "Search for someone to jump straight to their position in the chart.",
          },
          { term: "Print", desc: "Print the chart when you need a hard copy." },
        ],
      },
    ],
  },
  {
    id: "account",
    title: "Theme and signing out",
    blocks: [
      {
        kind: "p",
        text: "Use the theme toggle in the topbar to switch between light and dark mode. When you're done, open the account menu (the circle with your initial in the top-right) and choose Sign out.",
      },
    ],
  },
];

const adminGuideSections: GuideSection[] = [
  {
    id: "admin-records",
    title: "Creating and editing records",
    blocks: [
      {
        kind: "p",
        text: "Administrators can create and edit the core records that everyone else browses: People, Roles, Skills, Competencies, and Certifications.",
      },
      {
        kind: "terms",
        items: [
          {
            term: "Adding",
            desc: "Each directory has an Add button that opens a form to create a new record.",
          },
          {
            term: "Editing",
            desc: "Open a record's detail page to edit it. Changes are saved from there rather than inline in the list.",
          },
        ],
      },
    ],
  },
  {
    id: "admin-effective-dated",
    title: "Effective-dated person changes",
    blocks: [
      {
        kind: "p",
        text: "When you edit a person, some changes can be scheduled to take effect on a future date rather than immediately. This is useful for role moves, reporting-line changes, and other transitions you know about in advance.",
      },
      {
        kind: "p",
        text: "A scheduled change is recorded now but doesn't apply until its effective date arrives, at which point it takes effect automatically. Until then it shows as pending, and it can be canceled before it applies.",
      },
    ],
  },
  {
    id: "admin-bulk-certs",
    title: "Bulk certification assignment",
    blocks: [
      {
        kind: "p",
        text: "To assign the same certification to many people at once, use the People directory: select the people you want, then assign a certification to the whole selection in one step — no need to open each person individually.",
      },
    ],
  },
  {
    id: "admin-org-structure",
    title: "Managing org structure",
    blocks: [
      {
        kind: "p",
        text: "The organization's shape is made up of pillars, departments, and role teams. Administrators manage these building blocks so the directory, hierarchy, and org chart stay accurate as the organization changes.",
      },
    ],
  },
  {
    id: "admin-settings",
    title: "The Settings area",
    blocks: [
      {
        kind: "p",
        text: "Settings is admin-only and gathers the controls that shape the whole workspace:",
      },
      {
        kind: "terms",
        items: [
          {
            term: "Users",
            desc: "Create user accounts, set roles (user or admin), and reset passwords.",
          },
          {
            term: "Theme",
            desc: "Set brand colors and fonts. These overrides apply across the app, including the login page.",
          },
          {
            term: "Skill categories",
            desc: "Manage the categories used to group skills.",
          },
          {
            term: "Performance ratings",
            desc: "Define the five-point scale every skill and competency is rated against, from does-not-meet through consistently-exceeds.",
          },
          {
            term: "Role levels",
            desc: "Define the career ladder — Associate, Staff, Senior, Principal and an optional department or pillar head. Which levels apply is then chosen per role on that role's page; they don't have to be consecutive, and some roles have no entry level at all.",
          },
          {
            term: "Announcements",
            desc: "Post organization-wide notices that appear on everyone's dashboard.",
          },
          {
            term: "Audit log",
            desc: "The app-wide, filterable record of every change made in Enable.",
          },
        ],
      },
    ],
  },
  {
    id: "admin-audit",
    title: "The audit trail",
    blocks: [
      {
        kind: "p",
        text: "Every change in Enable is recorded automatically. There are two places to review it:",
      },
      {
        kind: "terms",
        items: [
          {
            term: "Per-record History tab",
            desc: "On any record's detail page, showing the changes made to that record.",
          },
          {
            term: "Settings → Audit log",
            desc: "The app-wide view, where you can filter across all changes made throughout the app.",
          },
        ],
      },
      {
        kind: "p",
        text: "Records resolve to readable names and values in both places, so you see meaningful entries rather than raw identifiers.",
      },
    ],
  },
];

function GuideBlockView({ block }: { block: GuideBlock }) {
  switch (block.kind) {
    case "p":
      return <p className="text-sm leading-relaxed text-foreground/90">{block.text}</p>;
    case "subheading":
      return <h3 className="mt-5 text-sm font-semibold text-foreground">{block.text}</h3>;
    case "list":
      return (
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-foreground/90">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case "terms":
      return (
        <dl className="space-y-2">
          {block.items.map((item) => (
            <div key={item.term} className="text-sm leading-relaxed">
              <dt className="inline font-medium text-foreground">{item.term}</dt>
              <dd className="inline text-foreground/90">
                {" — "}
                {item.desc}
              </dd>
            </div>
          ))}
        </dl>
      );
    default:
      return null;
  }
}

// Stable, content-derived key for a block. Blocks are static within a section
// and never reorder, but keying on the array index trips the lint rule — the
// content itself is unique per section, so it makes a good key.
function blockKey(block: GuideBlock): string {
  switch (block.kind) {
    case "p":
    case "subheading":
      return `${block.kind}:${block.text}`;
    case "list":
      return `list:${block.items.join("|")}`;
    case "terms":
      return `terms:${block.items.map((item) => item.term).join("|")}`;
    default:
      return "block";
  }
}

function GuideSectionView({ section }: { section: GuideSection }) {
  return (
    <Card id={section.id} className="scroll-mt-24">
      <CardHeader>
        <CardTitle>{section.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {section.blocks.map((block) => (
          <GuideBlockView key={blockKey(block)} block={block} />
        ))}
      </CardContent>
    </Card>
  );
}

function TableOfContents({ sections }: { sections: GuideSection[] }) {
  return (
    <nav aria-label="On this page" className="rounded-lg border bg-muted/30 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        On this page
      </p>
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
        {sections.map((section) => (
          <li key={section.id}>
            <a href={`#${section.id}`} className="text-primary transition-colors hover:underline">
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function HelpContent() {
  const { data: session } = useSession();
  const isAdmin = canViewAll(session?.user?.role);

  return (
    <div className="space-y-10">
      <section className="space-y-4">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight">User Guide</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            How to navigate Enable and get things done, for everyone.
          </p>
        </div>
        <TableOfContents sections={userGuideSections} />
        <div className="space-y-4">
          {userGuideSections.map((section) => (
            <GuideSectionView key={section.id} section={section} />
          ))}
        </div>
      </section>

      {isAdmin && (
        <section className="space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h2 className="font-display text-xl font-semibold tracking-tight">Admin Guide</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Additional capabilities available to administrators. This section is shown to admins
              only as a convenience — access to each action is enforced by the server.
            </p>
          </div>
          <TableOfContents sections={adminGuideSections} />
          <div className="space-y-4">
            {adminGuideSections.map((section) => (
              <GuideSectionView key={section.id} section={section} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
